import { computed, Injectable, signal } from '@angular/core';
import { ImpersonationService } from '../../dev-tools/impersonation.service';
import { ApplicationStatus } from '../models/onboarding-models';

const STORAGE_KEY = 'ccfx_application_status';

@Injectable({ providedIn: 'root' })
export class OnboardingStatusService {
  private readonly impersonationService = new ImpersonationService();
  private readonly _impersonation = this.impersonationService.getState();

  private readonly _status = signal<ApplicationStatus | null>(
    this._impersonation
      ? this._impersonation.status
      : ((localStorage.getItem(STORAGE_KEY) as ApplicationStatus | null) ?? null),
  );
  private readonly _companyId = signal<number | null>(null);
  private readonly _isCorporateAdmin = signal<boolean>(
    this._impersonation?.isCorporateAdmin ?? false,
  );
  private readonly _canTrade = signal<boolean>(this._impersonation?.canTrade ?? false);
  private readonly _canSign = signal<boolean>(this._impersonation?.canSign ?? false);
  private readonly _rateTierId = signal<number | null>(this._impersonation?.rateTierId ?? null);
  private readonly _rateTierName = signal<string | null>(this._impersonation?.rateTierName ?? null);
  private readonly _enabledCompanyPermissions = signal<Set<string>>(new Set());
  private readonly _firstName = signal<string | null>(null);
  /** Company's KYC re-review due date from whoami (ISO string), or null if not yet loaded. */
  private readonly _kycReReviewDate = signal<string | null>(null);
  /**
   * True when the user has already submitted a personal KYC application (user-onboarding).
   * Set on app init (via backend check) and immediately after submission.
   */
  private readonly _hasPersonalKycSubmission = signal<boolean>(false);
  /** Individual user status from whoami (e.g. 'Active', 'Pending'). */
  private readonly _userStatus = signal<string | null>(this._impersonation?.userStatus ?? null);

  readonly status = this._status.asReadonly();
  readonly companyId = this._companyId.asReadonly();
  readonly isCorporateAdmin = this._isCorporateAdmin.asReadonly();
  readonly canTrade = this._canTrade.asReadonly();
  readonly canSign = this._canSign.asReadonly();
  readonly canManageKycReview = computed(() => this._isCorporateAdmin() || this._canSign());
  readonly rateTierId = this._rateTierId.asReadonly();
  readonly rateTierName = this._rateTierName.asReadonly();
  readonly enabledCompanyPermissions = this._enabledCompanyPermissions.asReadonly();
  readonly firstName = this._firstName.asReadonly();
  readonly hasPersonalKycSubmission = this._hasPersonalKycSubmission.asReadonly();
  readonly userStatus = this._userStatus.asReadonly();
  readonly kycReReviewDate = this._kycReReviewDate.asReadonly();

  /** True once the company's KYC re-review due date has passed — trading is blocked. */
  readonly isKycReReviewOverdue = computed(() => {
    const date = this._kycReReviewDate();
    if (!date) return false;
    return new Date(date).getTime() <= new Date().setHours(0, 0, 0, 0);
  });

  /** True when the individual user account is Active (distinct from company status). */
  readonly isUserActive = computed(() => this._userStatus() === 'Active');

  /**
   * True when the user may access the main portal (home / dashboard).
   * Companies with 'Pending' or 'Invited' status must complete the onboarding form first.
   */
  readonly isPortalAccessible = computed(() => {
    const s = this._status();
    return s !== null && s !== 'Invited' && s !== 'Pending';
  });

  /** User can place CAD-to-foreign orders (requires canTrade + company permission, not overdue for KYC re-review). */
  readonly canPlaceCADOrder = computed(
    () =>
      this._canTrade() &&
      this._enabledCompanyPermissions().has('TXN_CAD_TO_FOREIGN') &&
      !this.isKycReReviewOverdue(),
  );

  /** User can place foreign-to-foreign orders (requires canTrade + company permission, not overdue for KYC re-review). */
  readonly canPlaceF2FOrder = computed(
    () =>
      this._canTrade() &&
      this._enabledCompanyPermissions().has('TXN_FOREIGN_TO_FOREIGN') &&
      !this.isKycReReviewOverdue(),
  );

  /** True when the user has trade permission and the company isn't overdue for KYC re-review. */
  readonly canPlaceOrder = computed(() => this._canTrade() && !this.isKycReReviewOverdue());

  getStatus(): ApplicationStatus | null {
    return this._status();
  }

  setStatus(status: ApplicationStatus): void {
    localStorage.setItem(STORAGE_KEY, status);
    this._status.set(status);
  }

  setCompanyId(id: number): void {
    this._companyId.set(id);
  }

  setIsCorporateAdmin(value: boolean): void {
    this._isCorporateAdmin.set(value);
  }

  setCanTrade(value: boolean): void {
    this._canTrade.set(value);
  }

  setCanSign(value: boolean): void {
    this._canSign.set(value);
  }

  setRateTierId(value: number | null): void {
    this._rateTierId.set(value);
  }

  setRateTierName(value: string | null): void {
    this._rateTierName.set(value);
  }

  setEnabledCompanyPermissions(keys: string[]): void {
    this._enabledCompanyPermissions.set(new Set(keys));
  }

  setFirstName(name: string): void {
    this._firstName.set(name);
  }

  setHasPersonalKycSubmission(value: boolean): void {
    this._hasPersonalKycSubmission.set(value);
  }

  setUserStatus(status: string): void {
    this._userStatus.set(status);
  }

  setKycReReviewDate(date: string | null): void {
    this._kycReReviewDate.set(date);
  }

  reset(): void {
    localStorage.removeItem(STORAGE_KEY);
    this._status.set(null);
    this._companyId.set(null);
    this._isCorporateAdmin.set(false);
    this._canTrade.set(false);
    this._canSign.set(false);
    this._rateTierId.set(null);
    this._rateTierName.set(null);
    this._enabledCompanyPermissions.set(new Set());
    this._firstName.set(null);
    this._hasPersonalKycSubmission.set(false);
    this._userStatus.set(null);
    this._kycReReviewDate.set(null);
  }
}
